import { useEffect, useState } from "react";
import axios from "axios";
import { ShoppingBag, Send, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { API } from "../lib/api";
import { useConfig } from "../context/ConfigContext";

export const EcommerceToggleCard = ({ authHeaders }) => {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const { refresh } = useConfig();

  useEffect(() => {
    axios
      .get(`${API}/admin/settings/ecommerce`, { headers: authHeaders })
      .then((r) => setData(r.data))
      .catch(() => toast.error("Webshop-instelling laden mislukt"));
  }, [authHeaders]);

  const toggle = async () => {
    const next = !data.enabled;
    const msg = next
      ? "Webshop AANZETTEN? Consumenten kunnen dan direct bestellen en betalen via Mollie."
      : "Webshop UITZETTEN? Consumenten kunnen dan alleen nog een aanvraag sturen.";
    if (!window.confirm(msg)) return;
    setSaving(true);
    try {
      const r = await axios.put(`${API}/admin/settings/ecommerce`, { enabled: next }, { headers: authHeaders });
      setData(r.data);
      refresh();
      toast.success(next ? "Webshop staat AAN — direct bestellen is live" : "Webshop staat UIT — aanvraag-modus actief");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Opslaan mislukt");
    } finally {
      setSaving(false);
    }
  };

  if (!data) return null;
  const on = data.enabled;

  return (
    <div data-testid="admin-ecommerce-settings" className={`border bg-[#0a0a0a] p-6 sm:p-8 ${on ? "border-[var(--tha-red)]" : "border-[var(--tha-border)]"}`}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6">
        <div className="flex items-start gap-3">
          <ShoppingBag size={18} className="text-[var(--tha-red)] mt-1.5" />
          <div>
            <div className="font-display text-2xl">ONLINE VERKOOP (WEBSHOP)</div>
            <div data-testid="ecommerce-status-text" className="text-sm font-mono-x text-[var(--tha-muted)] mt-2 max-w-xl leading-relaxed">
              {on
                ? "AAN — consumenten zien prijzen, winkelmandje en rekenen direct af via Mollie."
                : "UIT — consumenten sturen een bestelaanvraag (geen prijzen, geen mandje)."}{" "}
              Zakelijke aanvragen via /zakelijk blijven altijd actief.
            </div>
          </div>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={on}
          data-testid="ecommerce-toggle"
          onClick={toggle}
          disabled={saving}
          className={`relative shrink-0 w-20 h-10 border transition-colors disabled:opacity-50 ${on ? "bg-[var(--tha-red)] border-[var(--tha-red)]" : "bg-[#050505] border-[var(--tha-border)]"}`}
        >
          <span className={`absolute top-1 w-8 h-8 transition-transform duration-200 ${on ? "translate-x-[42px] bg-black" : "translate-x-1 bg-[var(--tha-muted)]"}`} style={{ left: 0 }} />
        </button>
      </div>
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-px bg-[var(--tha-border)]">
        <div className="bg-[#0a0a0a] p-4 flex items-center gap-3">
          {on ? <ShoppingBag size={16} className="text-[var(--tha-red)]" /> : <Send size={16} className="text-[var(--tha-muted)]" />}
          <div>
            <div className="text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)]">HUIDIGE MODUS</div>
            <div data-testid="ecommerce-mode-value" className="font-display text-xl">{on ? "DIRECT BESTELLEN" : "AANVRAAG-MODUS"}</div>
          </div>
        </div>
        <div className="bg-[#0a0a0a] p-4">
          <div className="text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)]">LAATST GEWIJZIGD</div>
          <div className="font-mono-x text-sm mt-1">{data.updated_at ? new Date(data.updated_at).toLocaleString("nl-NL") : "—"}</div>
        </div>
      </div>
      {!data.has_mollie_key && (
        <div data-testid="ecommerce-no-mollie-warning" className="mt-4 flex items-center gap-2 text-xs font-mono-x text-[var(--tha-red)]">
          <AlertTriangle size={14} /> Geen Mollie key ingesteld — de webshop kan pas aan nadat je hieronder een key opslaat.
        </div>
      )}
    </div>
  );
};
